/**
 * Mobile (below `lg`) layout of the contact band. Rendered by `../ContactUs.tsx` →
 * `ContactUsSection`, and directly by the pages listed in that file's header. Unlike
 * `../Desktop/ContactUs.tsx`, it has no `Media` wrapper of its own: every caller wraps it in
 * `Media lessThan="lg"`, and a new caller must too or it renders at every width.
 *
 * Related: `../Shared/ContactCard.tsx` (the cards and what a click does).
 */
import React from "react";
import styled from "@emotion/styled";
import { Container, ContentSection } from "@ryerson/frontend.layout";
import { useTheme } from "@ryerson/frontend.theme";
import { Typography } from "@ryerson/frontend.typography";
import { Column, Grid, Row } from "@ryerson/frontend.layout";
import ContactCard from "../Shared/ContactCard";
import { ContactUsComponentProps } from "../ContactUs";
import { css } from "@emotion/react";
import Spacer from "@components/Shared/Spacer/Spacer";
const Image = styled.div`
	display: block;
	height: 260px;
	width: calc(100% - 20px);
	background-repeat: no-repeat;
	margin-right: 20px;
	background-position: center center;
	background-size: cover;
`;

/**
 * Three layouts:
 * - `withImage`: the headline, then the photo on the left half and the cards stacked on the right
 *   (first card unless `oneButtonVariant`, then the second).
 * - no image, two cards: the headline, then the second card on the left and the first on the
 *   right.
 * - no image, `oneButtonVariant`: one full-width second card.
 *
 * In careers mode two cards differ from the rest: the second card of the `withImage` layout isn't
 * given `isCareers`, so it opens the Canada careers URL in the same tab; and the one-button card
 * shows `button2` rather than `newButton2` and isn't given `isCareers` either. No careers page
 * uses those two layouts on mobile today (they pass `withImage={false}` and
 * `oneButtonVariant={false}`).
 */
const ContactUsMobile: React.FC<ContactUsComponentProps> = ({
	withImage,
	content,
	background,
	oneButtonVariant,
	isCareers,
}) => {
	const { theme } = useTheme();
	const {
		title,
		titleSpecial,
		button1,
		button2,
		imageUrl,
		button1Link,
		button2Link,
		newButton1,
		newButton2,
	} = content;

	return (
		<ContentSection hPadding="0px" vPadding="0px" type={background}>
			{withImage ? (
				<>
					<Container hPadding="20px" vPadding="40px">
						<Typography
							css={css`
								display: inline;
								letter-spacing: -2px;
							`}
							variant="h3"
						>
							{title}&nbsp;
						</Typography>
						<Typography
							css={css`
								display: inline;
								letter-spacing: -2px;
							`}
							variant="h3"
							color={theme.colors.primary.gray}
						>
							{titleSpecial}
						</Typography>
					</Container>
					<Grid gutter={0}>
						<Row>
							<Column align="top" xs={6} sm={6} md={6}>
								<Image css={{ backgroundImage: "url(" + imageUrl + ")" }} />
							</Column>
							<Column xs={6} sm={6} md={6}>
								{oneButtonVariant === false ? (
									<>
										<ContactCard
											color={
												process.env.NEXT_PUBLIC_COMPANY ===
												"southernToolSteel"
													? theme.colors.primary.primaryBrand
													: theme.colors.primary.accentBrand
											}
											description={isCareers ? newButton1 : button1}
											textColor={theme.colors.primary.secondaryBrand}
											buttonColor={theme.colors.primary.secondaryBrand}
											buttonSize="xs"
											buttonLink={
												isCareers
													? `${process.env.NEXT_PUBLIC_CAREERS_URL_USA}`
													: button1Link
											}
											isCareers={isCareers}
										/>
										<Spacer
											css={css`
												height: 20px;
											`}
										/>
									</>
								) : (
									<></>
								)}
								<ContactCard
									color={theme.colors.primary.secondaryBrand}
									description={isCareers ? newButton2 : button2}
									textColor={theme.colors.primary.white}
									buttonLink={
										isCareers
											? `${process.env.NEXT_PUBLIC_CAREERS_URL_CANADA}`
											: button2Link
									}
									buttonColor={
										process.env.NEXT_PUBLIC_COMPANY === "southernToolSteel"
											? theme.colors.primary.primaryBrand
											: theme.colors.primary.accentBrand
									}
									foreground={"dark"}
									buttonSize="xs"
								/>
							</Column>
						</Row>
					</Grid>
					<Spacer
						css={css`
							height: 50px;
						`}
					/>
				</>
			) : (
				<Container vPadding="40px">
					<Grid>
						<Row>
							<Column
								xs={12}
								sm={12}
								md={12}
								style={css`
									margin-bottom: 20px;
								`}
							>
								<Typography
									css={css`
										display: inline;
										letter-spacing: -2px;
									`}
									variant="h3"
								>
									{title}&nbsp;
								</Typography>
								<Typography
									css={css`
										display: inline;
										letter-spacing: -2px;
									`}
									variant="h3"
									color={theme.colors.primary.gray}
								>
									{titleSpecial}
								</Typography>
							</Column>
						</Row>
						{oneButtonVariant === false ? (
							<Row>
								<Column
									xs={6}
									sm={6}
									md={6}
									style={css`
										padding-right: 5px;
									`}
								>
									<ContactCard
										color={theme.colors.primary.secondaryBrand}
										description={isCareers ? newButton2 : button2}
										buttonLink={
											isCareers
												? `${process.env.NEXT_PUBLIC_CAREERS_URL_CANADA}`
												: button2Link
										}
										textColor={theme.colors.primary.white}
										foreground="dark"
										buttonColor={
											process.env.NEXT_PUBLIC_COMPANY === "southernToolSteel"
												? theme.colors.primary.primaryBrand
												: theme.colors.primary.accentBrand
										}
										buttonSize="xs"
										withImage={false}
										isCareers={isCareers}
									/>
								</Column>
								<Column
									xs={6}
									sm={6}
									md={6}
									style={css`
										padding-left: 5px;
									`}
								>
									<ContactCard
										color={
											process.env.NEXT_PUBLIC_COMPANY === "southernToolSteel"
												? theme.colors.primary.primaryBrand
												: theme.colors.primary.accentBrand
										}
										description={isCareers ? newButton1 : button1}
										textColor={theme.colors.primary.secondaryBrand}
										buttonColor={theme.colors.primary.secondaryBrand}
										buttonSize="xs"
										withImage={false}
										buttonLink={
											isCareers
												? `${process.env.NEXT_PUBLIC_CAREERS_URL_USA}`
												: button1Link
										}
										isCareers={isCareers}
									/>
								</Column>
							</Row>
						) : (
							<Row>
								<Column xs={12} sm={12} md={12}>
									<ContactCard
										oneButtonVariant={true}
										color={theme.colors.primary.secondaryBrand}
										description={button2}
										buttonLink={
											isCareers
												? `${process.env.NEXT_PUBLIC_CAREERS_URL_CANADA}`
												: button2Link
										}
										textColor={theme.colors.primary.white}
										foreground="light"
										buttonColor={
											process.env.NEXT_PUBLIC_COMPANY === "southernToolSteel"
												? theme.colors.primary.secondaryBrand
												: theme.colors.primary.accentBrand
										}
										buttonSize="xs"
										withImage={false}
									/>
								</Column>
							</Row>
						)}
					</Grid>
				</Container>
			)}
		</ContentSection>
	);
};

export default ContactUsMobile;
